import { inject, Injectable, signal } from '@angular/core';
import { AlertService, AlertType } from 'src/app/shared/services/alert.service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, shareReplay, tap, throwError } from 'rxjs';
import { IEmployeeSimple } from 'src/app/shared/models/employee.model';
import { IPagination } from 'src/app/shared/models/pagination.model';
import {
  CreateReviewName,
  ExcludableParticipant,
  ParticipantType,
  PeerReviewStatusResponse,
  ReviewCompleteStatus,
  ReviewCycle,
  ReviewCycleGetAllPeersResponse,
  ReviewCyclePdfResponse,
  ReviewCyclePeerRequest,
  ReviewCyclePeerResponse,
  ReviewCyclePhase,
  ReviewCycleStage,
  ReviewCycleTemplate,
  Reviewee,
  ReviewTemplateStep,
  ReviewType,
  StagesByType,
  StepsByType,
  StepSelection,
} from 'src/app/shared/models/performance-management/review.model';
import { TranslateService } from '@ngx-translate/core';
import { NavigationExtras } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ReviewsService {
  private readonly alertService = inject(AlertService);
  private readonly httpClient = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  // api urls
  private readonly urlBase: string = 'api/performance-management';
  private readonly urlConfig: string = `${this.urlBase}/review-cycle-config`;
  private readonly urlManagement: string = `${this.urlBase}/review-cycle-management`;
  private readonly urlUser: string = `${this.urlBase}/review-cycle-user`;

  // signals
  filteredReviews = signal<IPagination<ReviewCycle>>({
    items: [],
    totalElements: 0,
    config: { sortColumns: [] },
  });
  filteredReviewees = signal<IPagination<Reviewee>>({
    items: [],
    totalElements: 0,
    config: { sortColumns: [] },
  });
  currentPeers = signal<ReviewCycleGetAllPeersResponse>({
    reviewers: [],
    reviewed: null,
  });
  stagesByType = signal<StagesByType>({});
  stepsByType = signal<StepsByType<ReviewType, ReviewTemplateStep[]>>({});
  reviewTypes = signal<Array<{ type: ReviewType }>>([]);

  constructor() {}

  // Reviews (review cycles, surveys, etc.)
  searchReviews(params: HttpParams, searchTerm: string = ''): void {
    this.httpClient
      .get<IPagination<ReviewCycle>>(this.urlConfig, {
        params: searchTerm ? params.append('name', searchTerm) : params,
      })
      .pipe(catchError(this.handleError), tap(this.filteredReviews.set))
      .subscribe();
  }

  // Reviews (review cycles, surveys, etc.)
  searchReviewsForUser(params: HttpParams, searchTerm: string = ''): void {
    this.httpClient
      .get<IPagination<ReviewCycle>>(this.urlUser, {
        params: searchTerm ? params.append('name', searchTerm) : params,
      })
      .pipe(catchError(this.handleError), tap(this.filteredReviews.set))
      .subscribe();
  }

  // Reviewees
  searchReviewees(
    id: number,
    params: HttpParams,
    searchTerm: string = '',
    type: ParticipantType,
  ): Observable<IPagination<Reviewee>> {
    if (type) {
      params = params.append('type', type);
    }
    const reviewees$ = this.httpClient
      .get<IPagination<Reviewee>>(`${this.urlConfig}/${id}/participant-selection`, {
        params: searchTerm ? params.append('name', searchTerm) : params,
      })
      .pipe(
        catchError(this.handleError),
        tap((response: IPagination<Reviewee>) => {
          const updatedItems = response.items.map((el) => {
            return {
              ...el,
              photo: el.photo ? `data:image/png;base64,${el.photo}` : null,
            };
          });
          this.filteredReviewees.set({
            ...response,
            items: updatedItems,
          });
        }),
        shareReplay(),
      );
    reviewees$.subscribe();
    return reviewees$;
  }

  getAssignedReviewees(id: number): Observable<Array<IEmployeeSimple>> {
    return this.httpClient
      .get<Array<IEmployeeSimple>>(`${this.urlConfig}/${id}/participant-selection/assigned`)
      .pipe(catchError(this.handleError));
  }

  getReviewById(id: number): Observable<ReviewCycle> {
    return this.httpClient
      .get<ReviewCycle>(`${this.urlConfig}/${id}`)
      .pipe(catchError(this.handleError));
  }

  getReviewByIdForUser(id: number): Observable<ReviewCycle> {
    return this.httpClient
      .get<ReviewCycle>(`${this.urlUser}/${id}`)
      .pipe(catchError(this.handleError));
  }

  createReviewCycle(
    cycleName: CreateReviewName['cycleName'],
    type: ReviewType,
  ): Observable<ReviewCycle> {
    return this.httpClient.post<ReviewCycle>(`${this.urlConfig}`, { cycleName, type }).pipe(
      catchError(this.handleError),
      tap(() => {
        this.handleSuccess(
          this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_CYCLE_CREATE'),
        );
      }),
    );
  }

  updateReviewCycle(
    reviewCycleId: number,
    cycleName: CreateReviewName['cycleName'],
  ): Observable<ReviewCycle> {
    return this.httpClient
      .put<ReviewCycle>(`${this.urlConfig}/${reviewCycleId}`, { cycleName })
      .pipe(catchError(this.handleError));
  }

  deleteReviewCycle(reviewCycleId: number): Observable<any> {
    return this.httpClient.delete(`${this.urlConfig}/${reviewCycleId}`).pipe(
      tap(() => {
        this.handleSuccess(
          this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_CYCLE_DELETE'),
        );
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  startReviewCycle(reviewCycleId: number): Observable<ReviewCycle> {
    return this.httpClient
      .post<ReviewCycle>(`${this.urlManagement}/${reviewCycleId}/start-cycle`, null)
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_CYCLE_START'),
          );
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  endReviewCycle(reviewCycleId: number): Observable<ReviewCycle> {
    return this.httpClient
      .post<ReviewCycle>(`${this.urlManagement}/${reviewCycleId}/finish-cycle`, null)
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_CYCLE_END'),
          );
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  //Peers
  getAllPeersForCurrentUser(cycleId: number): Observable<ReviewCycleGetAllPeersResponse> {
    return this.httpClient
      .get<ReviewCycleGetAllPeersResponse>(
        `api/performance-management/review-cycle/${cycleId}/peers`,
      )
      .pipe(
        tap((response) => console.log('Received peers response:', response)),
        catchError((error) => {
          console.error('Error getting peers:', error);
          return this.handleError(error);
        }),
      );
  }

  //This endpoint gets self, peer, upward and downward selected peers and return them as groups
  getAllPeersForCurrentUserBySteps(cycleId: number): Observable<StepSelection[]> {
    return this.httpClient
      .get<StepSelection[]>(`api/performance-management/review-cycle/${cycleId}/peers/all`)
      .pipe(
        map((steps) =>
          steps.map((step) => ({
            ...step,
            peerSelection: step.peerSelection.map((peer) => ({
              ...peer,
              assignedEmployee: {
                ...peer.assignedEmployee,
                photo: peer.assignedEmployee.photo,
              },
            })),
          })),
        ),
        catchError((error) => {
          console.error('Error getting peers by steps:', error);
          return this.handleError(error);
        }),
      );
  }

  deletePeerByUser(cycleId: number, employeeId: number): Observable<void> {
    return this.httpClient
      .delete<void>(`api/performance-management/review-cycle/${cycleId}/peers/${employeeId}`)
      .pipe(
        catchError((error) => {
          console.error('Error deleting peer:', error);
          return this.handleError(error);
        }),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEER_REMOVE'),
          );
        }),
      );
  }

  createPeerSelections(
    reviewCycleId: number,
    request: ReviewCyclePeerRequest,
  ): Observable<Array<ReviewCyclePeerResponse>> {
    return this.httpClient
      .post<
        Array<ReviewCyclePeerResponse>
      >(`api/performance-management/review-cycle/${reviewCycleId}/peers`, request)
      .pipe(
        catchError((error) => {
          console.error('Error creating peers:', error);
          return this.handleError(error);
        }),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEERS_SELECTION'),
          );
        }),
      );
  }

  updatePeerSelection(
    cycleId: number,
    request: ReviewCyclePeerRequest,
  ): Observable<Array<ReviewCyclePeerResponse>> {
    return this.httpClient
      .put<
        Array<ReviewCyclePeerResponse>
      >(`api/performance-management/review-cycle/${cycleId}/peers`, request)
      .pipe(
        catchError((error) => {
          console.error('Error updating peers:', error);
          return this.handleError(error);
        }),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEERS_SELECTION'),
          );
        }),
      );
  }

  selectParticipants(
    reviewCycleId: number,
    employeeIds: Array<number>,
  ): Observable<Array<Reviewee>> {
    return this.httpClient
      .post<Array<Reviewee>>(`${this.urlConfig}/${reviewCycleId}/participant-selection`, {
        employeeIds,
      })
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEWEES_SELECTION'),
          ),
        ),
      );
  }

  updateParticipants(
    reviewCycleId: number,
    employeeIds: Array<number>,
  ): Observable<Array<Reviewee>> {
    return this.httpClient
      .put<Array<Reviewee>>(`${this.urlConfig}/${reviewCycleId}/participant-selection`, {
        employeeIds,
      })
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEWEES_SELECTION'),
          ),
        ),
      );
  }

  updateLateParticipants(
    reviewCycleId: number,
    employeeIds: Array<number>,
  ): Observable<Array<Reviewee>> {
    return this.httpClient
      .post<Array<Reviewee>>(`${this.urlConfig}/${reviewCycleId}/late-participant-selection`, {
        employeeIds,
      })
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEWEES_SELECTION'),
        ),
      );
  }

  removeReviewee(reviewCycleId: number, employeeId: number): Observable<any> {
    return this.httpClient
      .delete(`${this.urlConfig}/${reviewCycleId}/participant-selection/${employeeId}`)
      .pipe(
        catchError(this.handleError),
        tap(() => this.handleSuccess('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEER_REMOVE')),
      );
  }

  getExcludableParticipants(reviewCycleId: number): Observable<ExcludableParticipant[]> {
    return this.httpClient.get<ExcludableParticipant[]>(
      `${this.urlConfig}/${reviewCycleId}/participant-selection/excludable`,
    );
  }

  excludeParticipants(reviewCycleId: number, employeeIds: number[]): Observable<number> {
    return this.httpClient.delete<number>(
      `${this.urlConfig}/${reviewCycleId}/participant-selection/exclusion`,
      { body: { employeeIds } },
    );
  }

  // Stages
  getStagesByType(type: ReviewType): Observable<ReviewCycleStage[]> {
    const cachedStagesByType = this.stagesByType();

    const reviewStagesObservable = cachedStagesByType[type]
      ? new Observable<ReviewCycleStage[]>((subscriber) =>
          subscriber.next(cachedStagesByType[type]),
        )
      : this.httpClient
          .get<ReviewCycleStage[]>(`${this.urlConfig}/stages/${type}`)
          .pipe(catchError(this.handleError), tap(this.updateStagesByType(type)));
    return reviewStagesObservable.pipe(shareReplay(1));
  }

  // Types
  getReviewTypes(): void {
    const reviewTypes = this.reviewTypes();
    const reviewTypesObservable = reviewTypes
      ? new Observable((subscriber) => subscriber.next(reviewTypes))
      : this.httpClient.get<any>(`${this.urlConfig}/types`);
    reviewTypesObservable.pipe(catchError(this.handleError), tap(this.reviewTypes.set)).subscribe();
  }

  // Steps
  getTemplateSteps(type: ReviewType): Observable<ReviewTemplateStep[]> {
    const cachedStepsByType = this.stepsByType();

    const reviewStepsObservable = cachedStepsByType[type]
      ? new Observable((subscriber) => subscriber.next(cachedStepsByType[type]))
      : this.httpClient
          .get<ReviewTemplateStep[]>(`${this.urlConfig}/steps/${type}`)
          .pipe(catchError(this.handleError), tap(this.updateStepsByType(type)));
    // @ts-ignore
    return reviewStepsObservable;
  }

  createReviewTemplates(reviewId: number, stepsTemplates: any): Observable<any> {
    return this.httpClient
      .post(`${this.urlConfig}/${reviewId}/templates-assignment`, stepsTemplates)
      .pipe(catchError(this.handleError));
  }

  updateReviewTemplates(reviewId: number, stepsTemplates: any): Observable<any> {
    return this.httpClient
      .put(`${this.urlConfig}/${reviewId}/templates-assignment`, stepsTemplates)
      .pipe(catchError(this.handleError));
  }

  getReviewTemplates(reviewId: number): Observable<Array<ReviewCycleTemplate>> {
    return this.httpClient
      .get<Array<ReviewCycleTemplate>>(`${this.urlConfig}/${reviewId}/templates-assignment`)
      .pipe(catchError(this.handleError));
  }

  getPhases(type: ReviewType): Observable<Array<ReviewCyclePhase>> {
    return this.httpClient
      .get<Array<ReviewCyclePhase>>(`${this.urlConfig}/phases/${type}`)
      .pipe(catchError(this.handleError));
  }

  getReviewCyclePhasesSchedule(reviewCycleId: number): Observable<Array<any>> {
    return this.httpClient
      .get<Array<any>>(`${this.urlConfig}/${reviewCycleId}/phases-schedule`)
      .pipe(catchError(this.handleError));
  }

  createPhasesSchedule(reviewCycleId: number, phasesSchedule: any): Observable<any> {
    return this.httpClient
      .post(`${this.urlConfig}/${reviewCycleId}/phases-schedule`, phasesSchedule)
      .pipe(catchError(this.handleError));
  }

  updatePhasesSchedule(reviewCycleId: number, phasesSchedule: any): Observable<any> {
    return this.httpClient
      .put(`${this.urlConfig}/${reviewCycleId}/phases-schedule`, phasesSchedule)
      .pipe(catchError(this.handleError));
  }

  sendTeamDataReminder(reviewCycleId: number): Observable<void> {
    return this.httpClient
      .post<void>(`${this.urlConfig}/${reviewCycleId}/team-data-reminder`, null)
      .pipe(catchError(this.handleError));
  }

  sendReminder(reviewCycleId: number): Observable<any> {
    return this.httpClient
      .post<any>(`${this.urlManagement}/${reviewCycleId}/send-reminder`, null)
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REMINDER_SEND'),
          );
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  startNextPhase(reviewCycleId: number): Observable<any> {
    return this.httpClient
      .post<any>(`${this.urlManagement}/${reviewCycleId}/next-phase`, null)
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_NEXT_PHASE_START'),
          );
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  sendPacket(reviewCycleId: number, employeeId: number): Observable<ReviewCycle> {
    return this.httpClient
      .post<ReviewCycle>(
        `${this.urlManagement}/${reviewCycleId}/send-packet?employeeId=${employeeId}`,
        null,
      )
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_PACKET_SEND'),
          );
        }),
      );
  }

  sendPacketForUser(reviewCycleId: number, employeeId: number): Observable<ReviewCycle> {
    return this.httpClient
      .post<ReviewCycle>(
        `${this.urlUser}/${reviewCycleId}/send-packet?employeeId=${employeeId}`,
        null,
      )
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_PACKET_SEND'),
          );
        }),
      );
  }

  buildNavigationExtras(
    item: PeerReviewStatusResponse,
    reviewCycleId: number,
    detailLabel?: string,
  ): NavigationExtras {
    return {
      state: {
        reviewData: {
          assignmentId: item.assignmentId,
          reviewCycleId: reviewCycleId,
          reviewerId: item.assignedEmployee.employeeId,
          templateId: item.templateId,
          detailLabel,
          reviewee: {
            name: item.assignedEmployee.name,
            position: item.assignedEmployee.position,
            photo: item.assignedEmployee.photo,
          },
          completed: item.reviewCompletionStatus === ReviewCompleteStatus.COMPLETED,
        },
      },
    };
  }

  downloadProbationReviewPdf(
    reviewCycleId: number,
    employeeId: number,
  ): Observable<ReviewCyclePdfResponse> {
    return this.httpClient.get<ReviewCyclePdfResponse>(
      `${this.urlConfig}/${reviewCycleId}/export-probation-review-pdf/${employeeId}`,
    );
  }

  downloadReviewAssignmentsByEmployeeAndCyclePdf(
    reviewCycleId: number,
    employeeId: number,
  ): Observable<ReviewCyclePdfResponse> {
    return this.httpClient.get<ReviewCyclePdfResponse>(
      `${this.urlConfig}/${reviewCycleId}/export-review-assignments-pdf/${employeeId}`,
    );
  }

  handleDownload(download$: Observable<ReviewCyclePdfResponse>, onToggle: (v: boolean) => void) {
    onToggle(true);
    download$.pipe(finalize(() => onToggle(false))).subscribe({
      next: (response) => {
        const byteCharacters = atob(response.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: response.contentType });
        this.saveBlob(blob, response.fileName);
      },
      error: (err) => this.handleError(err),
    });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private readonly handleError = (error: HttpErrorResponse): Observable<never> => {
    this.alertService.showAlert(error.error ?? error.statusText, AlertType.error);
    return throwError(() => error);
  };

  private handleSuccess(message: string) {
    this.alertService.showAlert(message, AlertType.success);
  }

  private updateStagesByType(type: ReviewType) {
    return (stages: ReviewCycleStage[]) =>
      this.stagesByType.update((types) => ({ ...types, [type]: stages }));
  }

  private updateStepsByType(type: ReviewType) {
    return (steps: ReviewTemplateStep[]) =>
      this.stepsByType.update((types) => ({ ...types, [type]: steps }));
  }
}
